/**
 * /library/:songId — Lyrixa Studio.
 *
 * A full page now, not a drawer. It used to render through Library's <Outlet /> as
 * a 420px panel over the song grid; the redesign makes it a production workspace
 * that owns the whole content area, because a drawer cannot hold a tool palette, a
 * timeline, a lyric sheet and a transport at the same time — in the drawer they
 * took turns, and opening a tool hid the song you were working on.
 *
 * What moved where, so nothing looks lost:
 *   - the overview's six tool cards + version history → the AI TOOLS rail
 *   - the overview's artwork/title/metadata row       → the ACTIVE SONG panel
 *   - the overview's player                           → the floating transport
 *   - the overview's lyrics disclosure                → the LYRICS column, open
 *   - the overview's details + prompt disclosures     → the timeline column
 *   - the overview's Delete button                    → ACTIVE SONG's More menu
 *   - every tool, version and compare panel           → the centre column
 *
 * Unchanged: the URL is still the source of truth (studioStore.songId is synced to
 * the route param, not the reverse), the keyboard shortcuts, and the deep-link guard.
 *
 * MIXED-MODE. The priced generation actions still run through the simulation/provider seam,
 * while the local audio-edit actions with backend support (trim, tempo, pitch, mastering,
 * etc.) dispatch real Studio routes. See src/hooks/useStudio.ts.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { songsApi } from '../api/songsApi';
import { LoadError } from '../components/ui';
import {
  ComparePanel,
  CoverPanel,
  DownloadsPanel,
  ExtendPanel,
  MasterPanel,
  MixPanel,
  RemixPanel,
  ReplacePanel,
  ShareDialog,
  StemsPanel,
  StudioWorkspace,
  TempoPitchPanel,
  Timeline,
  TrimPanel,
  UploadPanel,
  VersionsPanel,
} from '../components/studio';
import DrawerHeader from '../components/studio/DrawerHeader';
import type { MixPanelDraft } from '../components/studio';
import { FullscreenPlayer } from '../components/player';
import { downloadCoverImage } from '../lib/download';
import { formatTime, parseLength } from '../lib/formatting';
import { versionViewSong } from '../lib/versions';
import { NAV_DRAWER_QUERY, useMediaQuery, usePlayer, useSongs, useStudio, useStudioProjects } from '../hooks';
import {
  pausePreviewAudio,
  playPreviewAudioFromCurrentPosition,
  seekPreviewAudio,
  suspendPreviewAudio,
  togglePreviewAudio,
} from '../hooks/usePlayer';
import { selectProfileName, useAuthStore } from '../stores/authStore';
import { useSongsStore } from '../stores/songsStore';
import { useStudioStore } from '../stores/studioStore';
import { useUiStore } from '../stores/uiStore';
import type { Song } from '../types/domain';
import { paths } from './paths';

export default function Studio() {
  const { songId } = useParams<{ songId: string }>();
  const navigate = useNavigate();

  /**
   * Whether the share dialog is open.
   *
   * Local state, not uiStore: it is one boolean scoped to this screen, and nothing
   * else in the app opens or reads it. The same reasoning as Login's form fields.
   */
  const [sharing, setSharing] = useState(false);

  /**
   * Whether the full-screen player is open.
   *
   * Local for the same reason `sharing` is: it is one boolean belonging to this
   * screen. Deliberately NOT in playerStore — what is playing is application state
   * and several surfaces read it, but which view is on top of this route is not, and
   * putting it in the player store would be the "second player state" the brief
   * rules out in a different guise.
   */
  const [fullscreen, setFullscreen] = useState(false);
  const mobileTools = useMediaQuery(NAV_DRAWER_QUERY);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [mobileToolsView, setMobileToolsView] = useState<'list' | 'timeline' | 'panel'>('list');

  const {
    song,
    drawer,
    processing,
    processingActionKind,
    compareIds,
    stemsReady,
    stemsLoading,
    stemsChecked,
    stemsProcessing,
    stemExpectedCount,
    downloadableStemLabels,
    playingStemLabel,
    loadingStemLabel,
    openStudio,
    closeStudio,
    setDrawer,
    toggleCompare,
    clearCompare,
    setField,
    renameVersion,
    runExtend,
    runRemix,
    runReplace,
    runTrim,
    runCut,
    runSplit,
    runCrossfade,
    runInsertSilence,
    runFade,
    runNormalize,
    runTempo,
    runPitch,
    runMix,
    runMaster,
    runSplitStems,
    runGenerateCover,
    saveLyrics,
    runRegenerate,
    pendingCover,
    uploadCoverFile,
    removeCoverArt,
    applyPendingCover,
    discardPendingCover,
    deleteVersion,
    duplicateVersion,
    loadVersionForEditing,
    saveVersionToLibrary,
    downloadStem,
    playStem,
    downloadAllStems,
  } = useStudio();

  const { songs: librarySongs, toggleFavorite, downloadSong, downloadLyrics, printLyrics, getSong, uploadAudioFile } =
    useSongs();
  /*
    THE PROJECT THIS VISIT BELONGS TO, when there is one.

    A project scopes the workspace and nothing more: the MUSIC LIBRARY strip lists its
    tracks and Prev/Next walks them. Every tool, every version, the transport and the
    lyric sheet are unchanged and still act on the one song the URL names.

    Null is a real state and not a gap — the Library opens the workspace directly from a
    song row, and arriving that way is not working in a project — so `songs` below falls
    back to the whole library, which is exactly what this screen showed before projects
    existed. See routes/StudioEntry.tsx.
  */
  const { activeProject, songsIn } = useStudioProjects();
  const player = usePlayer();
  const showToast = useUiStore((s) => s.showToast);
  const setDeleteTarget = useUiStore((s) => s.setDeleteTarget);
  const setAddToPlaylistTarget = useUiStore((s) => s.setAddToPlaylistTarget);
  // §14. The Library's own edit target, not a Studio one: AppModals renders one
  // EditSongModal for the whole app and this is the field it watches.
  const setEditTarget = useUiStore((s) => s.setEditTarget);
  // The creator line for the full-screen player. The same selector the account menu
  // uses, so the name shown there and here cannot disagree; songs carry no artist
  // field of their own, and the account holder is who generated them.
  const profileName = useAuthStore(selectProfileName);

  // Tool form values. Selected one field at a time: Zustand compares selector
  // results with Object.is, so returning an object literal would re-render this
  // component on every unrelated store change.
  const form = {
    extendFromText: useStudioStore((s) => s.extendFromText),
    extendSecondsText: useStudioStore((s) => s.extendSecondsText),
    extendTemperatureText: useStudioStore((s) => s.extendTemperatureText),
    extendVarianceText: useStudioStore((s) => s.extendVarianceText),
    extendPrompt: useStudioStore((s) => s.extendPrompt),
    remixStyle: useStudioStore((s) => s.remixStyle),
    remixPrompt: useStudioStore((s) => s.remixPrompt),
    remixLyricsOverride: useStudioStore((s) => s.remixLyricsOverride),
    replaceFrom: useStudioStore((s) => s.replaceFrom),
    replaceTo: useStudioStore((s) => s.replaceTo),
    replaceDuration: useStudioStore((s) => s.replaceDuration),
    replaceSteps: useStudioStore((s) => s.replaceSteps),
    replacePrompt: useStudioStore((s) => s.replacePrompt),
    stemsShifts: useStudioStore((s) => s.stemsShifts),
    coverStyle: useStudioStore((s) => s.coverStyle),
    coverPrompt: useStudioStore((s) => s.coverPrompt),
  };

  const routeSong = getSong(songId);
  const songsLoadStatus = useSongsStore((s) => s.load.status);
  const editingHeadVersionId = useSongsStore((s) => s.editingHeadVersionId);
  const resetEditForm = useStudioStore((s) => s.resetEditForm);
  const [loadedVersionSong, setLoadedVersionSong] = useState<Song | null>(null);
  const [mixPreviewFocus, setMixPreviewFocus] = useState(true);
  const [mixPreviewLoading, setMixPreviewLoading] = useState(false);
  const [mixPreviewElapsed, setMixPreviewElapsed] = useState(0);
  const [mixPreviewDurationSec, setMixPreviewDurationSec] = useState<number | null>(null);
  const [mixHeadElapsed, setMixHeadElapsed] = useState(0);
  const [mixSessionDraft, setMixSessionDraft] = useState<MixPanelDraft | null>(null);
  const pauseOther = player.pauseOther;

  /*
    THE TRACKS THIS WORKSPACE OFFERS — the project's, or the whole library.

    Scoped to the project only when the open song is actually IN it. That condition is the
    important half: the Library can open the workspace on any song while a project happens
    to be the active one, and a strip listing a project the current track is not part of
    would be showing a set that does not contain what is on screen — with no entry marked
    current and Prev/Next walking somewhere else entirely.

    So leaving a project's tracks silently leaves its scope, which is the same thing the
    strip has always meant: these are the tracks you can switch to from here.
  */
  const projectSongs = activeProject ? songsIn(activeProject) : [];
  const inProject = Boolean(routeSong && projectSongs.some((s) => s.id === routeSong.id));
  const songs = inProject ? projectSongs : librarySongs;

  /*
    Sync the store to the URL. The monolith's openStudio() also stopped playback, which
    openStudio in the store deliberately does not do (it is a pure state mutation); that
    side effect is reproduced here.

    PAUSE, NOT STOP — and this was the second half of the playlist defect. `stop` empties
    the player, queue included, so arriving here from a playing playlist silently ended
    it: the tracks after this one were gone, and nothing on screen said so. Pausing keeps
    the loaded song, its position and the play order, so the queue survives the visit and
    the transport's Prev/Next below can walk it.

    Something still has to stop advancing, which is why this is not simply removed. The
    workspace shows ONE song — this one — and its transport, timeline and playhead all
    render the store's `elapsed`. Left running after a navigation, the clock would be
    advancing a *different* track while every readout here attributed the motion to this
    one, and the auto-advance would eventually swap the song out from under the page.

    And ONLY a different track, which is what pauseOther's argument is for: opening the
    Studio on the song you are already listening to must not silence it, and a queue
    advancing into this page (Next moves the player, then navigates) must not stop on
    arrival. Both are the same case — the store's song is already this one, so there is
    no discrepancy to correct.
  */
  useEffect(() => {
    if (!songId) return;
    openStudio(songId);
    pauseOther(songId);
    return () => closeStudio();
  }, [songId, openStudio, closeStudio, pauseOther]);

  useEffect(() => {
    if (mobileTools) return;
    setMobileToolsOpen(false);
    setMobileToolsView('list');
  }, [mobileTools]);

  const openProjects = () => navigate(paths.studioProjects);
  const close = openProjects;

  /*
    Keyboard shortcuts, carried over verbatim: Space = play/pause,
    Escape = back out one level, Delete/Backspace = delete the song. Suppressed
    while a text field has focus so typing a prompt cannot trigger them.

    `drawer` is in the dependency list because Escape's behaviour depends on it,
    and `song` because Space and Delete both act on it.
  */
  useEffect(() => {
    if (!song) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = document.activeElement?.tagName;
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

      if (e.key === 'Escape') {
        e.preventDefault();
        if (mobileTools && mobileToolsOpen) {
          setMobileToolsOpen(false);
          setMobileToolsView('list');
        } else if (drawer) setDrawer(null);
        else close();
        return;
      }
      if (typing) return;
      if (e.key === ' ') {
        e.preventDefault();
        player.toggle(song.id);
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        setDeleteTarget(song);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // `close` and `player` are recreated each render; including them would
    // reinstall the listener on every keystroke. The identities they close over
    // (navigate, the player store's stable actions) do not change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song, drawer, setDrawer, setDeleteTarget]);

  // A deep link to a song that no longer exists (deleted, or a stale shared URL)
  // returns to the Library rather than rendering an empty workspace — but only once the
  // library read has actually answered. In backend mode the store now starts empty on
  // purpose, so redirecting during the initial load would turn every valid deep link into
  // a bounce back to the Library before GET /songs completed.
  useEffect(() => {
    if (songId && !routeSong && songsLoadStatus === 'ready') {
      navigate(paths.library, { replace: true });
    }
  }, [songId, routeSong, songsLoadStatus, navigate]);

  useEffect(() => {
    if (drawer === 'mix') {
      setMixPreviewFocus(true);
      return;
    }
    setMixPreviewLoading(false);
    setMixPreviewFocus(true);
    setMixPreviewElapsed(0);
    setMixPreviewDurationSec(null);
  }, [drawer]);

  const previousSongIdRef = useRef(songId);
  const previousEditingHeadVersionIdRef = useRef(editingHeadVersionId);
  const drawerRef = useRef(drawer);
  drawerRef.current = drawer;

  useEffect(() => {
    const songChanged = previousSongIdRef.current !== songId;
    const editingHeadChanged = previousEditingHeadVersionIdRef.current !== editingHeadVersionId;

    previousSongIdRef.current = songId;
    previousEditingHeadVersionIdRef.current = editingHeadVersionId;

    setMixPreviewFocus(true);
    setMixPreviewLoading(false);
    setMixPreviewElapsed(0);
    setMixPreviewDurationSec(null);
    setMixHeadElapsed(0);
    setMixSessionDraft(null);

    if (songChanged || editingHeadChanged) {
      setField('replaceFrom', '');
      setField('replaceTo', '');
      setField('replaceDuration', '');
      setField('replaceSteps', '30');
      setField('replacePrompt', '');
    }

    if (songChanged || (editingHeadChanged && drawerRef.current !== 'trim')) {
      resetEditForm();
    }
  }, [songId, editingHeadVersionId, resetEditForm, setField]);

  useEffect(() => {
    if (player.songId === songId) setMixHeadElapsed(player.elapsed);
  }, [player.elapsed, player.songId, songId]);

  useEffect(() => {
    if (player.previewSongId !== songId) {
      if (drawer !== 'mix') {
        setMixPreviewElapsed(0);
        setMixPreviewDurationSec(null);
      }
      return;
    }
    setMixPreviewElapsed(player.previewElapsed);
    setMixPreviewDurationSec(player.previewDurationSec);
  }, [drawer, player.previewDurationSec, player.previewElapsed, player.previewSongId, songId]);

  const editingHeadVersion = useMemo(
    () => (routeSong ? routeSong.versions.find((version) => version.id === editingHeadVersionId) ?? null : null),
    [routeSong, editingHeadVersionId]
  );
  const displaySong = useMemo(
    () => (song && editingHeadVersion ? versionViewSong(song, editingHeadVersion, loadedVersionSong) : song),
    [song, editingHeadVersion, loadedVersionSong]
  );

  useEffect(() => {
    if (!song || !editingHeadVersion) {
      setLoadedVersionSong(null);
      return;
    }

    let cancelled = false;
    setLoadedVersionSong(null);
    void songsApi.getVersionSong(song.id, editingHeadVersion.id).then((versionSong) => {
      if (!cancelled) setLoadedVersionSong(versionSong);
    }).catch((error) => {
      if (!cancelled) {
        console.error('[studio] getVersionSong failed', error);
        setLoadedVersionSong(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [song, editingHeadVersion]);

  if (!song) {
    if (songsLoadStatus === 'error') {
      return <LoadError message="Your library could not be loaded, so Lyrixsa Studio cannot open this song yet." />;
    }
    return null;
  }

  const workspaceSong = displaySong ?? song;
  // Version-aware display fields with the parent song id preserved for actions that still
  // route through the visible library song.
  const actionSong = displaySong ? { ...displaySong, id: song.id } : song;

  // Two facts, not one: `playing` labels the button and animates the waveform, `active`
  // decides whether the store's position belongs to this song. They differ exactly while
  // paused, which is the state this route now arrives in. See usePlayer.
  const playing = player.isPlaying(song.id);
  const active = player.isActive(song.id);
  const mixDrawerOpen = drawer === 'mix';
  const previewAvailable = player.previewSongId === song.id;
  const previewFocused = mixDrawerOpen && mixPreviewFocus;
  const previewHasAudio = previewAvailable || mixPreviewDurationSec !== null || mixPreviewElapsed > 0;
  const previewTransportElapsed = previewAvailable ? player.previewElapsed : mixPreviewElapsed;
  const previewTransportDurationSec = previewAvailable
    ? (player.previewDurationSec ?? mixPreviewDurationSec ?? parseLength(workspaceSong.length))
    : (mixPreviewDurationSec ?? parseLength(workspaceSong.length));
  const previewTransportTotalLabel =
    previewAvailable && player.previewDurationSec
      ? player.previewDurationLabel
      : mixPreviewDurationSec
        ? formatTime(mixPreviewDurationSec)
        : workspaceSong.length;
  const fallbackPreviewPlaying = previewFocused && !previewHasAudio ? playing : false;
  const transportPlaying = previewFocused ? (previewHasAudio ? player.previewPlaying : fallbackPreviewPlaying) : playing;
  const transportActive = previewFocused ? true : active;
  const transportElapsed = previewFocused ? (previewHasAudio ? previewTransportElapsed : mixHeadElapsed) : player.elapsed;
  const transportDurationSec = previewFocused ? (previewHasAudio ? previewTransportDurationSec : player.durationSec) : player.durationSec;
  const transportTotalLabel = previewFocused
    ? (previewHasAudio
        ? previewTransportTotalLabel
        : active && player.durationSec > 0
          ? formatTime(player.durationSec)
          : workspaceSong.length)
    : active && player.durationSec > 0
      ? formatTime(player.durationSec)
      : workspaceSong.length;
  const pauseHeadSong = () => {
    if (playing) player.toggleSource(song.id);
  };

  const pauseCurrentPreviewMode = () => {
    if (previewHasAudio) {
      suspendPreviewAudio();
      return;
    }
    pauseHeadSong();
  };

  const toggleMixPreviewMode = () => {
    if (mixPreviewFocus) {
      pauseCurrentPreviewMode();
      setMixPreviewFocus(false);
      return;
    }
    pauseHeadSong();
    setMixPreviewFocus(true);
    if (previewHasAudio) playPreviewAudioFromCurrentPosition();
  };

  const toggleTransportPlay = () => {
    if (previewFocused) {
      if (previewHasAudio) {
        if (playing) player.toggleSource(song.id);
        togglePreviewAudio();
        return;
      }
      player.toggleSource(song.id);
      return;
    }
    if (previewHasAudio) {
      pausePreviewAudio();
      player.toggleSource(song.id);
      return;
    }
    player.toggleSource(song.id);
  };

  const seekTransport = (seconds: number) => {
    if (previewFocused) {
      if (previewHasAudio) {
        if (playing) player.toggleSource(song.id);
        seekPreviewAudio(seconds);
        return;
      }
      player.seek(song.id, seconds);
      setMixHeadElapsed(Math.round(seconds));
      return;
    }
    if (previewHasAudio) {
      pausePreviewAudio();
    }
    player.seek(song.id, seconds);
  };

  const openMobileToolsList = () => {
    setMobileToolsOpen(true);
    setMobileToolsView('list');
  };
  const closeMobileToolsSheet = () => {
    setMobileToolsOpen(false);
    setMobileToolsView('list');
  };
  const openMobileTimeline = () => {
    setDrawer(null);
    setMobileToolsOpen(true);
    setMobileToolsView('timeline');
  };
  const openTool = (panel: Exclude<typeof drawer, null>) => {
    setDrawer(panel);
    if (mobileTools) {
      setMobileToolsOpen(true);
      setMobileToolsView('panel');
    }
  };
  const back = () => {
    if (mobileTools) {
      setMobileToolsView('list');
      return;
    }
    setDrawer(null);
  };
  const closeToolSurface = mobileTools ? closeMobileToolsSheet : close;

  /*
    THE POSITION THE LYRIC SHEET FOLLOWS — Batch 6 §10, and a fraction rather than seconds
    on purpose. The sheet's sections are positioned by their share of the TEXT (see
    lib/sections), so the only thing they can be compared against is the track's share of
    its length; handing the panel `elapsed` would imply the sheet knew what a second means
    to a lyric, and nothing in the app does.

    The ACTIVE transport duration wins when the player has a real one, which is what keeps
    untimed fallback highlighting honest for a loaded version whose runtime differs from the
    parent song's declared length.

    `active`, not `playing`, for the same reason the timeline's playhead uses it: a pause
    should leave the highlight where the track stopped rather than dropping it back to the
    first verse. Null when this song is not the loaded one, which is what makes the sheet
    static for a track you are only editing.
  */
  const lyricPosition =
    active ? player.elapsed / (player.durationSec > 0 ? player.durationSec : parseLength(workspaceSong.length)) : null;
  const lyricElapsedSec = active ? player.elapsed : null;

  /*
    Cover export, shared by the Download Center row and the Cover Art preview's
    own download button so there is one behaviour rather than two.

    An uploaded cover really does export — it is the user's own image and there are
    bytes to write. Generated artwork still cannot: it is a gradient computed at
    render time from `artworkSeed`, with no file behind it. The three messages keep
    those cases distinct rather than collapsing them into one "not available".
  */
  const downloadCover = () => {
    if (actionSong.coverUrl) {
      const filename = downloadCoverImage(actionSong, actionSong.coverUrl);
      showToast(`Saved ${filename}`);
      return;
    }
    showToast(
      actionSong.artworkSeed
        ? 'This cover is placeholder artwork — there is no image file to export. Upload your own image to download it.'
        : 'Add cover art first'
    );
  };

  /*
    A panel's back arrow returns to the timeline; its X still leaves the Studio for
    the Library, which is what it did as a drawer. Both are preserved rather than
    re-pointed: the arrow is "close this tool", the X is "I'm done with this song".
  */
  const toolProps = {
    song: actionSong,
    form,
    setField,
    processing,
    onBack: back,
    onClose: closeToolSurface,
  };

  /*
    The centre column's content when a tool is open. Null means the timeline shows,
    which is also the fallback for a stale `compare` with fewer than two selections.
  */
  const panel =
    drawer === 'extend' ? (
      <ExtendPanel {...toolProps} processingActionKind={processingActionKind} onGenerate={runExtend} />
    ) : drawer === 'remix' ? (
      <RemixPanel {...toolProps} processingActionKind={processingActionKind} onGenerate={() => runRemix(form.remixStyle)} />
    ) : drawer === 'replace' ? (
      <ReplacePanel {...toolProps} processingActionKind={processingActionKind} onGenerate={runReplace} />
    ) : drawer === 'trim' ? (
      /*
        The only tool panel that takes no `form`, no `processing` and no generate
        handler, because there is nothing to charge, nothing to dispatch and nothing
        to wait for — trimming audio is not a capability that exists (see
        api/audioEditApi.ts). It gets the song, and the two ways out.
      */
      <TrimPanel
        song={actionSong}
        processing={processing}
        onBack={back}
        onClose={closeToolSurface}
        editingHeadVersion={editingHeadVersion}
        onTrim={runTrim}
        onCut={runCut}
        onSplit={runSplit}
        onCrossfade={runCrossfade}
        onInsertSilence={runInsertSilence}
        onFade={runFade}
        onNormalize={runNormalize}
      />
    ) : drawer === 'tempo' ? (
      /*
        Same shape as Trim now: the arithmetic is still local, but tempo and pitch can dispatch
        a real backend edit when that capability is present.
      */
      <TempoPitchPanel
        song={actionSong}
        processing={processing}
        onBack={back}
        onClose={closeToolSurface}
        editingHeadVersion={editingHeadVersion}
        onApplyTempo={runTempo}
        onApplyPitch={runPitch}
      />
    ) : drawer === 'mix' ? (
      <MixPanel
        song={actionSong}
        processing={processing}
        onBack={back}
        onClose={closeToolSurface}
        onApplyMix={runMix}
        editingHeadVersion={editingHeadVersion}
        previewTransportFocused={mixDrawerOpen && mixPreviewFocus}
        previewResumeElapsed={mixPreviewElapsed}
        onPreviewLoadingChange={setMixPreviewLoading}
        sessionDraft={mixSessionDraft}
        onSessionDraftChange={setMixSessionDraft}
      />
    ) : drawer === 'master' ? (
      <MasterPanel
        song={actionSong}
        processing={processing}
        onBack={back}
        onClose={closeToolSurface}
        editingHeadVersion={editingHeadVersion}
        onMaster={runMaster}
      />
    ) : drawer === 'upload' ? (
      /*
        Takes even less than Trim does: not even the song. A file the user brings in
        becomes a NEW library entry rather than a version of the one open, so the panel
        has nothing to do with the current track — it is in the Studio because that is
        where audio work lives and where the brief asks for it. `onUpload` sends it to
        POST /uploads/audio and adds the result to the library. See
        api/audioUploadApi.ts.
      */
      <UploadPanel onBack={back} onClose={closeToolSurface} onUpload={uploadAudioFile} />
    ) : drawer === 'stems' ? (
      <StemsPanel
        song={actionSong}
        processing={processing}
        onBack={back}
        onClose={closeToolSurface}
        processingActionKind={processingActionKind}
        stemsReady={stemsReady}
        stemsLoading={stemsLoading}
        stemsChecked={stemsChecked}
        stemsProcessing={stemsProcessing}
        stemExpectedCount={stemExpectedCount}
        stemLabels={downloadableStemLabels}
        playingStemLabel={playingStemLabel}
        loadingStemLabel={loadingStemLabel}
        shifts={form.stemsShifts}
        onShiftsChange={(value) => setField('stemsShifts', value)}
        onSplit={runSplitStems}
        onPlayStem={playStem}
        onDownloadStem={downloadStem}
        onDownloadAll={downloadAllStems}
      />
    ) : drawer === 'cover' ? (
      <CoverPanel
        {...toolProps}
        processingActionKind={processingActionKind}
        onGenerate={() => runGenerateCover(form.coverStyle)}
        onDownloadCover={downloadCover}
        pendingCover={pendingCover}
        onUpload={uploadCoverFile}
        onRemoveCover={removeCoverArt}
        onApplyCover={applyPendingCover}
        onDiscardCover={discardPendingCover}
      />
    ) : drawer === 'downloads' ? (
      /* The tier travels with the format (Batch 7 §10): the panel owns which quality is
         selected, and the request that would be sent carries both. Which one wins if they
         disagree is decided at the boundary — see audioEditApi.buildExportAudioRequest.

         `onDownloadDetails` routes through `downloadSong(song, 'DETAILS_ONLY')`, an
         internal sentinel meaning "write the honest details text file and do not treat
         this as an audio export". useSongs strips the sentinel back out before it builds
         the filename, records the download, or chooses the toast copy, so the saved file
         is `<title>_details.txt` and no fake format leaks into user-visible output. It was
         previously wired to `downloadAllStems`, so the "Download details (text)" row saved
         a stem-package manifest and toasted "Downloading stems ZIP" — a byte-identical
         duplicate of the Stems button above it, for a separation nobody asked for, and it
         skipped the `recordDownload` the MP3/WAV rows in this same panel perform.
         lib/__tests__/download.test.ts already described this row's intended output;
         nothing clicked it. */
      <DownloadsPanel
        song={actionSong}
        onBack={back}
        onClose={closeToolSurface}
        onDownloadAudio={(format) => void downloadSong(actionSong, format)}
        onDownloadLyrics={() => downloadLyrics(actionSong)}
        onDownloadCover={downloadCover}
        showStemsDownload={downloadableStemLabels.length > 0}
        onDownloadStems={downloadAllStems}
        onDownloadDetails={() => void downloadSong(actionSong, 'DETAILS_ONLY')}
      />
    ) : drawer === 'versions' ? (
      <VersionsPanel
        song={song}
        compareIds={compareIds}
        onBack={back}
        onClose={closeToolSurface}
        onToggleVersion={(versionId) =>
          player.isActiveVersion(song.id, versionId)
            ? player.toggleVersion(song.id, versionId)
            : player.toggleBackgroundVersion(song.id, versionId)
        }
        // "Cue + retarget editing": load the version into the transport (paused, its own
        // runtime) and make it the base the edit tools act on. See useStudio.loadVersionForEditing.
        onLoadVersion={(versionId) => {
          player.loadVersion(song.id, versionId);
          loadVersionForEditing(versionId);
        }}
        isVersionActive={(versionId) => player.isActiveVersion(song.id, versionId)}
        isVersionPlaying={(versionId) => player.isPlayingVersion(song.id, versionId)}
        isVersionLoadedForEditing={(versionId) => editingHeadVersionId === versionId}
        isVersionAuditionActive={(versionId) => player.backgroundVersionSongId === song.id && player.backgroundVersionId === versionId}
        isVersionAuditionPlaying={(versionId) =>
          player.backgroundVersionSongId === song.id && player.backgroundVersionId === versionId && player.backgroundVersionPlaying}
        onToggleCompare={toggleCompare}
        onRename={(versionId, currentLabel) => {
          const name = window.prompt('Rename version', currentLabel);
          if (name && name.trim()) renameVersion(song.id, versionId, name.trim());
        }}
        onDuplicate={duplicateVersion}
        onSaveToLibrary={saveVersionToLibrary}
        onDelete={deleteVersion}
        onOpenCompare={() => {
          setDrawer('compare');
          if (mobileTools) {
            setMobileToolsOpen(true);
            setMobileToolsView('panel');
          }
        }}
      />
    ) : drawer === 'compare' && compareIds.length === 2 ? (
      <ComparePanel
        song={song}
        compareIds={compareIds}
        onBack={() => {
          setDrawer('versions');
          if (mobileTools) setMobileToolsView('panel');
        }}
        onClose={closeToolSurface}
        onToggleVersion={(versionId) => player.toggleVersion(song.id, versionId)}
        isVersionActive={(versionId) => player.isActiveVersion(song.id, versionId)}
        isVersionPlaying={(versionId) => player.isPlayingVersion(song.id, versionId)}
        onLoad={(versionId) => {
          player.loadVersion(song.id, versionId);
          loadVersionForEditing(versionId);
          setDrawer('versions');
          clearCompare();
          if (mobileTools) setMobileToolsView('panel');
        }}
        onDeleteOther={(versionId) => {
          deleteVersion(versionId);
          setDrawer('versions');
          clearCompare();
          if (mobileTools) setMobileToolsView('panel');
        }}
        onReset={() => {
          clearCompare();
          setDrawer('versions');
          if (mobileTools) setMobileToolsView('panel');
        }}
      />
    ) : null;

  const mobileTimelinePanel = (
    <div className="lyx-tool-panel">
      <DrawerHeader title="Timeline" onBack={openMobileToolsList} onClose={closeMobileToolsSheet} />
      <Timeline
        song={workspaceSong}
        playing={transportPlaying}
        active={transportActive}
        elapsed={transportElapsed}
        speed={player.speed}
      />
    </div>
  );
  const mobileToolSheetContent = mobileTools
    ? mobileToolsView === 'timeline'
      ? mobileTimelinePanel
      : mobileToolsView === 'panel'
        ? panel
        : null
    : null;
  const mobileToolCurrentLabel =
    mobileToolsView === 'timeline' ? 'Timeline' : drawer === 'compare' ? 'Compare Versions' : undefined;

  /*
    Previous/Next, over the QUEUE when there is one and the library otherwise.

    The queue comes first because it is the order the user chose. Arriving here from a
    playing playlist used to be impossible to honour — the mount effect called `stop`,
    which emptied the queue, and `toggle` emptied it again — so this route walked the
    library unconditionally and the comment here said the queue branch could never run.
    Both of those clears are gone (see the effect above and playerStore.toggle), so the
    queue now survives into the Studio and pressing Next inside a playlist follows the
    playlist rather than jumping to whatever happens to be adjacent in the library.

    Two DIFFERENT actions behind one pair of buttons, and that is the point rather than a
    compromise: the queue branch moves the PLAYER, the library branch moves the PAGE.
    Both keep the workspace and the transport showing the same song, because the queue
    branch navigates as well — the store's song and the route's song must not diverge, or
    the timeline would be drawn for one track and the clock for another.

    Absent rather than disabled-and-wrapping at the ends, in both branches: in a
    five-track playlist, "next" meaning "back to the first" is not what a transport means.
  */
  const index = songs.findIndex((s) => s.id === song.id);
  const goTo = (i: number) => navigate(paths.song(songs[i].id));

  const queued = player.queueIndex >= 0;
  /** Follows the queue: moves the player, then brings the page with it. */
  const stepQueue = (move: () => void, to: string) => () => {
    move();
    navigate(paths.song(to));
  };
  const prevQueued = queued ? player.queue[player.queueIndex - 1] : undefined;
  const nextQueued = queued ? player.queue[player.queueIndex + 1] : undefined;

  const onPrev = queued
    ? prevQueued
      ? stepQueue(player.playPrevious, prevQueued)
      : undefined
    : index > 0
      ? () => goTo(index - 1)
      : undefined;
  const onNext = queued
    ? nextQueued
      ? stepQueue(player.playNext, nextQueued)
      : undefined
    : index >= 0 && index < songs.length - 1
      ? () => goTo(index + 1)
      : undefined;

  return (
    <>
      <StudioWorkspace
        song={song}
        displaySong={displaySong ?? undefined}
        mobile={mobileTools}
        mobileToolsOpen={mobileToolsOpen}
        onOpenMobileTools={openMobileToolsList}
        onCloseMobileTools={closeMobileToolsSheet}
        onOpenMobileTimeline={openMobileTimeline}
        mobileToolSheetContent={mobileToolSheetContent}
        mobileToolCurrentLabel={mobileToolCurrentLabel}
        songs={songs}
        // Named for what the strip is actually listing. Left undefined outside a project,
        // so the strip keeps its own "Music library" default and this visit looks exactly
        // as it did before projects existed.
        songsHeading={inProject && activeProject ? activeProject.name : undefined}
        // A stale `compare` still belongs to Version History as far as the rail is
        // concerned, so the rail does not go blank mid-flow.
        drawer={drawer === 'compare' ? 'versions' : drawer}
        panel={panel}
        onSelectTool={(panel) => {
          if (panel === null) {
            setDrawer(null);
            return;
          }
          openTool(panel);
        }}
        playing={transportPlaying}
        active={transportActive}
        elapsed={transportElapsed}
        durationSec={transportDurationSec}
        totalLabel={transportTotalLabel}
        volume={player.volume}
        speed={player.speed}
        repeat={player.repeat}
        muted={player.muted}
        previewMode={mixDrawerOpen ? previewFocused : false}
        previewLoading={mixDrawerOpen && previewFocused && mixPreviewLoading}
        previewLabel={mixDrawerOpen ? 'Preview mode' : undefined}
        onTogglePreviewMode={mixDrawerOpen ? toggleMixPreviewMode : undefined}
        onTogglePlay={toggleTransportPlay}
        onSetVolume={player.setVolume}
        onSetSpeed={player.setSpeed}
        onToggleRepeat={player.toggleRepeat}
        onToggleMute={player.toggleMute}
        // Preview mode seeks the live preview audio directly once one exists; before that,
        // the transport still drives the head song so the scrubber never goes dead.
        onSeek={seekTransport}
        onOpenFullscreen={() => setFullscreen(true)}
        onToggleFavorite={() => toggleFavorite(song.id)}
        onShare={() => setSharing(true)}
        onDownload={() => downloadSong(actionSong)}
        onCopyLyrics={() => {
          navigator.clipboard?.writeText(actionSong.lyrics || '');
          showToast('Lyrics copied');
        }}
        onDownloadLyrics={() => downloadLyrics(actionSong)}
        onPrintLyrics={() => printLyrics(actionSong)}
        // §10 — `lyricPosition` remains the untimed fallback, while exact-timed songs read
        // the same playback clock in seconds through `lyricElapsedSec`.
        lyricPosition={lyricPosition}
        lyricElapsedSec={lyricElapsedSec}
        onSaveLyrics={saveLyrics}
        onRegenerate={runRegenerate}
        processing={processing}
        processingActionKind={processingActionKind}
        // The same dialog the library row raises, against the song this route is open
        // on — one "Add to playlist" behaviour reached from two places, not two.
        onAddToPlaylist={() => setAddToPlaylistTarget(song)}
        /*
          §14: metadata editing, reachable from the Studio. The SAME modal the Library
          raises, on the same uiStore target, so there is exactly one metadata form in the
          app — see components/modals/AppModals.tsx, which renders it, and Library.tsx,
          which sets the same target from a song row. A Studio-specific editor would be a
          second definition of the song's own fields.
        */
        onEditMetadata={() => setEditTarget(song)}
        onDelete={() => setDeleteTarget(song)}
        onClose={closeToolSurface}
        hrefFor={paths.song}
        onPrev={onPrev}
        onNext={onNext}
      />

      {/*
        The full-screen player. Every control is the same handler the transport bar's
        equivalent uses, so the two are views of one playerStore rather than two
        players — pause in here and the transport shows paused.
      */}
      <FullscreenPlayer
        open={fullscreen}
        song={actionSong}
        artist={profileName}
        playing={playing}
        active={active}
        elapsed={player.elapsed}
        loading={player.loading && active}
        totalLabel={player.durationLabel}
        durationSec={player.durationSec}
        volume={player.volume}
        speed={player.speed}
        repeat={player.repeat}
        muted={player.muted}
        onClose={() => setFullscreen(false)}
        onTogglePlay={() => player.toggle(song.id)}
        onSeek={(seconds) => player.seek(song.id, seconds)}
        onSetVolume={player.setVolume}
        onSetSpeed={player.setSpeed}
        onToggleRepeat={player.toggleRepeat}
        onToggleMute={player.toggleMute}
        onPrev={onPrev}
        onNext={onNext}
      />

      {/*
        The share dialog. It now owns the discoverability gate as well as the share actions,
        because the user asked for the link not to appear until the song has been made
        discoverable from inside the dialog itself.
      */}
      <ShareDialog open={sharing} onClose={() => setSharing(false)} song={actionSong} />

    </>
  );
}
